import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatDateIT } from "@/lib/date";
import { toast } from "sonner";
import { ArrowLeft, ChevronRight, Search, ArrowUp, ArrowDown } from "lucide-react";
import { itemGroupColor } from "@/components/PlanItemsView";
import { absMedia } from "@/components/ExercisesTab";

export default function LoadHistoryPage({ role, clients = [] }) {
  const [plans, setPlans] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(role === "client" ? "self" : null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [planSort, setPlanSort] = useState("desc"); // by assigned_at / created_at
  const [logSort, setLogSort] = useState("desc"); // by session_date

  useEffect(() => {
    (async () => {
      try {
        const [p, e] = await Promise.all([api.get("/training-plans"), api.get("/exercises")]);
        setPlans(p.data); setExercises(e.data);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);

  useEffect(() => {
    if (!selectedPlanId) { setLogs([]); return; }
    api.get(`/training-plans/${selectedPlanId}/logs`).then(r => setLogs(r.data)).catch(() => {});
  }, [selectedPlanId]);

  const exMap = Object.fromEntries(exercises.map(e => [e.id, e]));
  const clientMap = Object.fromEntries(clients.map(c => [c.id, c]));

  // Admin: list clients WITH plans
  const clientsWithPlans = useMemo(() => {
    if (role !== "admin") return [];
    const ids = new Set(plans.map(p => p.client_id));
    return clients.filter(c => ids.has(c.id))
      .filter(c => !q || (c.display_name || c.name || "").toLowerCase().includes(q.toLowerCase()))
      .sort((a, b) => (a.display_name || a.name).localeCompare(b.display_name || b.name));
  }, [plans, clients, q, role]);

  const clientPlans = useMemo(() => {
    const arr = plans.filter(p => role === "client" ? true : p.client_id === selectedClientId);
    arr.sort((a, b) => {
      const k1 = a.created_at || "", k2 = b.created_at || "";
      return planSort === "asc" ? k1.localeCompare(k2) : k2.localeCompare(k1);
    });
    return arr;
  }, [plans, role, selectedClientId, planSort]);

  const selectedPlan = plans.find(p => p.id === selectedPlanId);

  // Compute progress per exercise: for each item, get all logs across the client's plans (for comparison)
  const clientId = role === "client" ? null : selectedClientId;
  const comparisonLogs = useMemo(() => {
    if (!selectedPlan) return {};
    const clientPlansIds = plans.filter(p => role === "client" || p.client_id === clientId).map(p => p.id);
    // We'll fetch on demand in future; for now show only current plan logs
    return {};
  }, [selectedPlan, plans, role, clientId]);

  if (role === "admin" && !selectedClientId) {
    return (
      <div>
        <div className="label-caps mb-2">Storico carichi</div>
        <h2 className="font-heading font-black uppercase tracking-tighter text-3xl mb-6">Clienti con schede ({clientsWithPlans.length})</h2>
        <div className="relative max-w-md mb-4">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Cerca cliente…" data-testid="load-history-client-search"
            className="pl-9 pr-3 py-2 bg-transparent border border-white/15 focus:border-[#A855F7] outline-none text-sm text-white w-full" />
        </div>
        <div className="grid gap-2">
          {clientsWithPlans.map(c => {
            const count = plans.filter(p => p.client_id === c.id).length;
            return (
              <button key={c.id} data-testid={`load-history-client-${c.id}`}
                onClick={() => setSelectedClientId(c.id)}
                className="card-surface p-4 flex items-center justify-between hover:border-[#A855F7] transition-colors text-left">
                <div>
                  <div className="font-heading font-bold uppercase tracking-tight">{c.display_name || c.name}</div>
                  <div className="text-[10px] text-white/50 mt-1">{count === 1 ? "1 scheda" : `${count} schede`}</div>
                </div>
                <ChevronRight size={16} className="text-white/40" />
              </button>
            );
          })}
          {clientsWithPlans.length === 0 && <div className="card-surface p-8 text-white/40 text-sm text-center">Nessun cliente ha ancora ricevuto una scheda.</div>}
        </div>
      </div>
    );
  }

  if (!selectedPlanId) {
    const title = role === "admin" ? (clientMap[selectedClientId]?.display_name || "Cliente") : "Le mie schede allenamento";
    return (
      <div>
        {role === "admin" && (
          <button onClick={() => setSelectedClientId(null)} className="text-white/60 hover:text-white text-xs uppercase tracking-widest inline-flex items-center gap-2 mb-4">
            <ArrowLeft size={14} /> Clienti
          </button>
        )}
        <div className="label-caps mb-2">Storico carichi</div>
        <h2 className="font-heading font-black uppercase tracking-tighter text-3xl mb-4">{title} · {clientPlans.length === 1 ? "1 scheda" : `${clientPlans.length} schede`}</h2>
        <button data-testid="plan-sort-toggle" onClick={() => setPlanSort(s => s === "asc" ? "desc" : "asc")}
          className="mb-4 text-white/60 hover:text-white text-[10px] uppercase tracking-widest inline-flex items-center gap-1">
          Data {planSort === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
        </button>
        <div className="grid gap-2">
          {clientPlans.map(p => {
            const expired = p.valid_until && p.valid_until < new Date().toISOString().slice(0, 10);
            return (
              <button key={p.id} data-testid={`load-history-plan-${p.id}`}
                onClick={() => setSelectedPlanId(p.id)}
                className={`card-surface p-4 flex items-center justify-between hover:border-[#A855F7] transition-colors text-left ${expired ? "opacity-60" : ""}`}>
                <div>
                  <div className="font-heading font-bold uppercase tracking-tight">{p.name}</div>
                  <div className="text-[10px] text-white/50 mt-1">
                    {(p.items || []).length} esercizi · Assegnata {p.assigned_at ? formatDateIT(p.assigned_at.slice(0,10)) : "—"}
                    {p.valid_until && ` · ${expired ? "Scaduta" : "Valida fino"} ${formatDateIT(p.valid_until)}`}
                  </div>
                </div>
                <ChevronRight size={16} className="text-white/40" />
              </button>
            );
          })}
          {clientPlans.length === 0 && <div className="card-surface p-8 text-white/40 text-sm text-center">Nessuna scheda ancora.</div>}
        </div>
      </div>
    );
  }

  // Detail view: logs by exercise
  const logsByItem = {};
  logs.forEach(l => { (logsByItem[l.item_id] = logsByItem[l.item_id] || []).push(l); });
  Object.values(logsByItem).forEach(arr => arr.sort((a, b) => {
    const k1 = a.session_date || "", k2 = b.session_date || "";
    return logSort === "asc" ? k1.localeCompare(k2) : k2.localeCompare(k1);
  }));

  return (
    <div>
      <button onClick={() => setSelectedPlanId(null)} className="text-white/60 hover:text-white text-xs uppercase tracking-widest inline-flex items-center gap-2 mb-4">
        <ArrowLeft size={14} /> Schede
      </button>
      <div className="label-caps mb-1">Storico schede</div>
      <h2 className="font-heading font-black uppercase tracking-tighter text-3xl mb-6">{selectedPlan?.name}</h2>

      <div className="space-y-6" data-testid="load-plan-days">
        {(selectedPlan?.days || []).map((day, dIdx) => (
          <div key={day.id || dIdx} data-testid={`load-day-${dIdx}`} className="border border-white/10 p-3 md:p-4">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <div className="font-heading font-black uppercase tracking-tighter text-lg">{day.name || `Giorno ${dIdx + 1}`}</div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">· {(day.items || []).length} eserciz{(day.items || []).length === 1 ? "io" : "i"}</div>
            </div>
            <div className="space-y-3">
              {(day.items || []).map((it, idx) => {
                const ex = exMap[it.exercise_id];
                const itemLogs = logsByItem[it.id] || [];
                const color = itemGroupColor(it, day.items || []);
                const groupLabel = it.group_type === "superset" ? "Super-set" : it.group_type === "circuit" ? "Circuito" : null;
                const boxStyle = color ? { borderColor: color.border, background: color.bg, borderWidth: 2 } : {};
                return (
                  <div key={it.id} data-testid={`load-item-${it.id}`}
                    style={boxStyle}
                    className={`card-surface p-4 ${color ? "" : ""}`}>
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <div className="w-8 h-8 flex items-center justify-center border border-white/10 font-heading font-bold text-sm shrink-0">{idx + 1}</div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-heading font-bold uppercase tracking-tight text-base">{ex?.name || "—"}</div>
                          {groupLabel && color && (
                            <span
                              data-testid={`load-item-${it.id}-group`}
                              style={{ background: color.border, color: "#fff" }}
                              className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest">
                              {groupLabel} {color.label}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-white/50">{it.sets} × {it.reps} · rec. {it.rest_seconds}s{ex?.muscle_group ? ` · ${ex.muscle_group}` : ""}</div>
                      </div>
                    </div>
                    {ex && (ex.media_urls || []).length > 0 && (
                      <div className="flex gap-2 mb-3 overflow-x-auto" data-testid={`load-item-${it.id}-media`}>
                        {ex.media_urls.map((u, k) => (
                          <a key={k} href={absMedia(u)} target="_blank" rel="noreferrer" className="border border-white/10 w-16 h-16 shrink-0 overflow-hidden">
                            <img src={absMedia(u)} alt="" className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                    {itemLogs.length === 0 && <div className="text-white/40 text-xs">Nessuna sessione registrata per questo esercizio.</div>}
                    {itemLogs.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs min-w-[420px]">
                          <thead>
                            <tr className="border-b border-white/5">
                              <th className="label-caps text-left p-2">
                                <button data-testid={`log-sort-${it.id}`} onClick={() => setLogSort(s => s === "asc" ? "desc" : "asc")}
                                  className="flex items-center gap-1 hover:text-white uppercase tracking-widest text-[10px]">
                                  Data {logSort === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
                                </button>
                              </th>
                              <th className="label-caps text-left p-2">Serie · Carichi/Reps</th>
                              <th className="label-caps text-left p-2">Note/Sensazioni</th>
                            </tr>
                          </thead>
                          <tbody>
                            {itemLogs.map(l => (
                              <tr key={l.id} className="border-b border-white/5">
                                <td className="p-2 whitespace-nowrap font-medium">{formatDateIT(l.session_date)}</td>
                                <td className="p-2 text-white/70">
                                  {(l.sets_data || []).map((s, i) => (
                                    <span key={i} className="inline-block mr-3">
                                      <span className="text-white/40">S{s.set_num}:</span>{" "}
                                      <span className="text-white">{s.weight != null ? `${s.weight}kg` : "—"}</span>
                                      {" × "}
                                      <span className="text-white">{s.reps ?? "—"}</span>
                                    </span>
                                  ))}
                                </td>
                                <td className="p-2 text-white/50 italic">{l.notes || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
              {(day.items || []).length === 0 && <div className="text-white/40 text-xs px-1">Nessun esercizio in questo giorno.</div>}
            </div>
          </div>
        ))}
        {(selectedPlan?.days || []).length === 0 && <div className="card-surface p-8 text-white/40 text-sm text-center">Scheda senza giorni.</div>}
      </div>
    </div>
  );
}
